import { LitElement, html, css, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { localized, msg, str } from "@lit/localize";

import { navigateOnClick } from "../../../../../common/router/app-navigation.js";
import { datumZeitleiste, uhrzeitKurz } from "../../../../../common/format/de-format.js";
import { ROUTE_SITZUNGEN, sitzungPfad } from "../../../../../config/navigation.js";
import { unterlagenStand } from "../../../../sitzungen/sitzungen-liste.js";
import { STADT_NAME } from "../../../../../config/app-config.js";
import type { startseite } from "@progressive-development/politik-model";
import { blockStyles } from "../../../../../common/styles/block-styles.js";
import {
  auswahlZeile,
  bezirkMarker,
  stadtweitZurueck,
} from "../../../../../common/components/block-parts.js";
import { AUSWAHL_TERMINE } from "./auswahl-texte.js";

/**
 * Block 4 — Termine: „Öffentliche Sitzungen in <Ort>“.
 *
 * DASSELBE BILD WIE /sitzungen, nicht dasselbe Bauteil: Die Zeilen sind
 * Objekt-Zeilen wie in der Kalender-Liste (Datum Mono links, Titel Serif,
 * Meta Mono, die Ebene als 4-px-Strich in ihrem Datenton). pd-calendar selbst
 * bräuchte hier das ganze Sitzungs-Artefakt aller Jahre — für fünf Termine aus
 * dem Aufmacher des Gebiets wäre das die falsche Last.
 *
 * Was fehlt, wird benannt, nicht kaschiert: Kommende Sitzungen haben in aller
 * Regel weder Ort noch Tagesordnung (von 36 künftigen Sitzungen tragen 2 einen
 * Ort und 0 eine Tagesordnung — die Tagesordnung entsteht erst kurz vorher).
 * Statt leerer Felder steht am Eintrag eine ruhige Statuszeile; das ist wahr,
 * und es ist ein Grund wiederzukommen.
 *
 * Ort und Tagesordnung fehlen im Aufmacher — beides ist bei kommenden Sitzungen
 * so gut wie nie belegt. Statt leerer Felder oder einer Behauptung steht hier
 * nichts; die Sitzungsseite zeigt es, sobald die Quelle es liefert.
 *
 * Der Hinweis der Quelle zur Öffentlichkeit steht EINMAL unter der Liste, nicht
 * an jedem Eintrag: Es gibt nichts zu filtern — die Schnittstelle liefert nur
 * den öffentlichen Teil, und ohne diesen Satz läse sich die Liste als
 * vollständig.
 */
@customElement("app-termine-block")
@localized()
export class AppTermineBlock extends LitElement {
  /** Die nächsten Sitzungen des gemerkten Gebiets, stadtweite eingeschlossen. */
  @property({ attribute: false }) termine: startseite.TerminEintrag[] = [];

  /** Name des Bezirks für den Leerzustand; leer heißt stadtweit. */
  @property() gebietName = "";

  /** Ob gerade ein Gebiet gemerkt ist — dann trägt der Block den Rückweg. */
  @property({ type: Boolean }) gefiltert = false;

  /** Wörtlicher Hinweis der Quelle zur Öffentlichkeit der Tagesordnungen. */
  @property() hinweis = "";

  static override styles = [
    blockStyles,
    css`
      ul.liste > li {
        border-top: none;
      }
      ul.liste > li + li {
        margin-top: var(--portal-space-2);
      }
      /* Die Objekt-Zeile: Haarlinie oben, die Ebene als Strich links, sonst kein Rahmen. */
      .termin {
        display: grid;
        grid-template-columns: 9rem minmax(0, 1fr);
        gap: var(--portal-space-4);
        align-items: baseline;
        padding: var(--portal-space-4) var(--portal-space-3);
        border-top: 1px solid var(--portal-linie);
        border-left: 4px solid var(--ebene, transparent);
        color: inherit;
        text-decoration: none;
      }
      .termin:hover {
        background: var(--portal-flaeche);
      }
      .termin:hover .termin-name {
        color: var(--portal-akzent);
      }
      .zeit {
        font-family: var(--portal-font-mono);
        font-size: var(--portal-size-meta);
        font-weight: 500;
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
      }
      .zeit small {
        display: block;
        font-size: inherit;
        font-weight: 400;
        color: var(--portal-ink-2);
      }
      .termin-name {
        font-family: var(--portal-font-body);
        font-size: var(--portal-size-md);
        line-height: 1.35;
        max-width: var(--portal-measure);
      }
      .termin-meta {
        display: flex;
        flex-wrap: wrap;
        gap: var(--portal-space-1) var(--portal-space-3);
        margin-top: var(--portal-space-2);
        font-family: var(--portal-font-mono);
        font-size: var(--portal-size-meta);
        color: var(--portal-ink-2);
      }

      /*
       * Der Unterlagenstand teilt die Zeile mit Gremium und Bezirk und erbt
       * deren Mono-Meta-Form. Drei Abstufungen, KEINE Signalfarbe: Eine
       * abgesagte Sitzung ist eine Tatsache, keine Warnung.
       */
      .stand.gedaempft {
        opacity: 0.7;
      }
      .stand.abgesagt {
        color: var(--portal-ink);
        font-weight: 500;
      }

      @media (max-width: 640px) {
        .termin {
          grid-template-columns: 1fr;
          gap: var(--portal-space-2);
        }
      }
    `,
  ];

  /** Die drei Ebenen als Datentöne — dieselben wie in der Kalender-Liste. */
  private _ebenenTon(ebene: string | undefined): string {
    if (ebene === "rat") return "var(--portal-daten)";
    if (ebene === "ausschuss") return "var(--portal-daten-45)";
    if (ebene === "bv") return "var(--portal-daten-25)";
    return "transparent";
  }

  /**
   * Was zu diesem Termin vorliegt — dieselbe Frage und dieselbe Funktion wie
   * auf `/sitzungen`, damit die Antwort nicht an zwei Stellen anders ausfaellt.
   *
   * OHNE die Zahl der Punkte: In der Kalender-Zeile steht sie ("liegt vor
   * (22 Punkte)"), hier waere sie eine dritte Zahl in einer Zeile, die schon
   * Datum und Uhrzeit traegt. Wer es genau wissen will, klickt.
   *
   * "Abgesagt" ist der wichtigste der Zustaende: Abgesagte Sitzungen werden
   * bewusst NICHT weggelassen — wer den Termin erwartet hat, soll erfahren,
   * dass er entfaellt.
   */
  private _standMarke(e: startseite.TerminEintrag) {
    switch (unterlagenStand(e)) {
      case "abgesagt":
        return html`<span class="stand abgesagt"
          >${msg("Abgesagt", { id: "app.start.termine.abgesagt" })}</span
        >`;
      case "tagesordnung":
        return html`<span class="stand"
          >${msg("Tagesordnung liegt vor", { id: "app.start.termine.to" })}</span
        >`;
      case "tagesordnung-folgt":
        return html`<span class="stand gedaempft"
          >${msg("Tagesordnung folgt", { id: "app.start.termine.toFolgt" })}</span
        >`;
      default:
        // Kommende Sitzungen — Niederschrift-Zustaende kommen hier nicht vor.
        return nothing;
    }
  }

  private _renderTermin(e: startseite.TerminEintrag) {
    // Der Pfad-Helfer, NICHT von Hand zusammengesetzt: Die Route heißt
    // `/sitzungen/:id`, hier stand `/sitzung/:id` — jeder Klick lief ins Leere.
    const ziel = sitzungPfad(e.id);
    return html`
      <li>
        <a
          class="termin"
          style="--ebene: ${this._ebenenTon(e.ebene)}"
          href=${ziel}
          @click=${(ev: Event) => navigateOnClick(ev, ziel)}
        >
          <span class="zeit"
            >${datumZeitleiste(e.start)}<small
              >${uhrzeitKurz(e.start)} ${msg("Uhr", { id: "app.start.termine.uhr" })}</small
            ></span
          >
          <span>
            <span class="termin-name">${e.name}</span>
            <span class="termin-meta">
              ${e.gremium ? html`<span>${e.gremium}</span>` : nothing}
              <!--
                Der Ort steht nicht im Aufmacher — von den kommenden Sitzungen
                traegt fast keine einen. Der Stand der Unterlagen steht seit
                der Pipeline-Erweiterung darin und wird gezeigt: Er beantwortet
                die Frage, die man sich VOR dem Klick stellt.
              -->
              ${this._standMarke(e)} ${bezirkMarker(e.bezirk)}
            </span>
          </span>
        </a>
      </li>
    `;
  }

  override render() {
    return html`
      <h2>
        ${msg(str`Öffentliche Sitzungen in ${this.gebietName || STADT_NAME}`, {
          id: "app.start.termine.titel",
        })}
      </h2>
      ${auswahlZeile(AUSWAHL_TERMINE())}
      ${
        this.termine.length === 0
          ? html`<p class="hinweis">
              ${
                this.gebietName
                  ? msg(
                      str`Keine Sitzung in ${this.gebietName} in den nächsten Wochen angesetzt.`,
                      {
                        id: "app.start.termine.leer.gebiet",
                      },
                    )
                  : msg("Keine Sitzung in den nächsten Wochen angesetzt.", {
                      id: "app.start.termine.leer",
                    })
              }
            </p>`
          : html`<ul class="liste">
              ${this.termine.map((e) => this._renderTermin(e))}
            </ul>`
      }

      <!-- Wörtlicher Hinweis der Quelle — einmal, nicht an jedem Eintrag. -->
      ${this.hinweis ? html`<p class="hinweis">${this.hinweis}</p>` : nothing}

      <a
        class="weiter"
        href="/${ROUTE_SITZUNGEN}"
        @click=${(e: Event) => navigateOnClick(e, `/${ROUTE_SITZUNGEN}`)}
        >${msg("Alle Sitzungen", { id: "app.start.termine.alle" })} →</a
      >${stadtweitZurueck(this.gefiltert)}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "app-termine-block": AppTermineBlock;
  }
}
